import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import {
  ChakraFlex,
  ChakraVStack,
  EmptySectionCard,
  FAIconName,
  FireflyButton,
  Text,
} from '@equip.health/ui';
import { Section } from '~/components/common/Section';
import { TreatmentEpisodeDetailsSectionCard } from './sections/section-cards/TreatmentEpisodeDetailsSectionCard';
import SectionHeader from '~/components/common/SectionHeader';
import { DischargeDetailsSectionCard } from './sections/section-cards/DischargeDetailsSectionCard';
import LastAttendedSectionCard from './sections/section-cards/LastAttendedSessionSectionCard';
import {
  useGetDischargePlanByIdQuery,
  useGetDischargePlanTasksQuery,
} from '~/store/services/clinical-data/dischargePlan';
import DischargePlanHeaderSection from './DischargePlanHeaderSection';
import {
  getDischargeStatuses,
  selectDischargePlanEpisode,
} from './util/dischargePlanUtils';
import { useCallback, useMemo, useState } from 'react';
import EditDischargeDetailsModalForm from './modals/EditDischargeDetailsModalForm';
import {
  TaskCardProps,
  Tasks,
  DischargePlanTaskType,
} from '~/components/common/Tasks';
import { getTaskProps } from '~/components/maud/inquiries/InquiryDetail/viewUtil';
import NewTaskAccordion from '~/components/maud/tasks/NewTaskAccordion';
import { useGetPatientQuery } from '~/store/services/patients';
import { useUpdateTaskStatusMutation } from '~/store/services/task';
import { DischargePlanViewContextProvider } from './DischargePlanViewContext';
import AftercareAndReferralsSections from './sections/AftercareAndReferralsSections';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { TaskEvents } from '~/analytics/taskEvents';
import { CREATE_TASK_SOURCES } from '~/lib/constants/task.constants';
import { useInsuranceAuthorizationEndDate } from '~/lib/hooks/useInsuranceAuthorizationEndDate';
import { useFlags } from 'launchdarkly-react-client-sdk';

const DischargePlanViewSections = ({ patientId }: { patientId?: string }) => {
  const { trackEvent } = useAnalytics();
  const [showEditDischargeDetailsModal, setShowEditDischargeDetailsModal] =
    useState(false);

  const {
    displayInsuranceAuthEndingDates: isDisplayInsuranceAuthEndingDatesEnabled,
    enableDisplayInsurancePart1: isEnableDisplayInsurancePart1,
  } = useFlags<LDFlags>();

  const isInsuranceAuthEndingDatesActive =
    isDisplayInsuranceAuthEndingDatesEnabled && isEnableDisplayInsurancePart1;

  const {
    data: treatmentEpisodesData,
    isLoading: isTreatmentEpisodesLoading,
    isError: isTreatmentEpisodesError,
  } = useGetPatientTreatmentEpisodesQuery(patientId, { skip: !patientId });

  const { episode: selectedEpisode, isDischarged } = useMemo(
    () => selectDischargePlanEpisode(treatmentEpisodesData?.data ?? []),
    [treatmentEpisodesData?.data],
  );

  const {
    data: dischargePlanData,
    isLoading: isGetDischargePlanLoading,
    isError: isGetDischargePlanError,
  } = useGetDischargePlanByIdQuery(selectedEpisode?.dischargePlan?.id, {
    skip: !selectedEpisode || !selectedEpisode.dischargePlan?.id,
  });

  const { isNotStarted, isPrepping, isSigned } = getDischargeStatuses(
    dischargePlanData?.status,
  );

  // A discharged episode with an unsigned plan should behave as if no episode exists
  const isDischargedAndUnsigned = isDischarged && !isSigned;

  const isEditable = isPrepping && !isDischarged;
  const showEmptyState =
    (!selectedEpisode && !isTreatmentEpisodesLoading) ||
    isNotStarted ||
    isDischargedAndUnsigned;
  const showEpisodeDetails = !!selectedEpisode && !isDischargedAndUnsigned;
  const showDischargePlanContent = isSigned || isEditable;

  const {
    data: dischargePlanTasksData,
    isFetching: isDischargePlanTasksFetching,
    isLoading: isDischargePlanTasksLoading,
  } = useGetDischargePlanTasksQuery(
    {
      dischargePlanId: selectedEpisode?.dischargePlan?.id ?? '',
      pageNumber: 1,
      pageSize: 25,
    },
    {
      skip: !selectedEpisode?.dischargePlan?.id || isSigned || isDischarged,
    },
  );

  const { data: patient } = useGetPatientQuery(
    { patientId },
    { skip: !patientId },
  );

  const { insuranceAuthorizationEndDate } = useInsuranceAuthorizationEndDate({
    patientId,
    enabled: isInsuranceAuthEndingDatesActive,
  });

  const taskProps = useMemo<TaskCardProps[]>(() => {
    return getTaskProps(dischargePlanTasksData).filter(
      (task) => task?.dischargePlanTaskType !== DischargePlanTaskType.SIGNED,
    );
  }, [dischargePlanTasksData]);

  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [isAddTaskExpanded, setIsAddTaskExpanded] = useState(false);

  const setShowAddTask = useCallback((show: boolean) => {
    if (show) {
      setIsAddTaskOpen(true);

      setTimeout(() => {
        setIsAddTaskExpanded(true);
      }, 0);
    } else {
      setIsAddTaskOpen(false);
      setIsAddTaskExpanded(false);
    }
  }, []);

  const [updateTaskStatus] = useUpdateTaskStatusMutation();
  const handleAddTask = () => {
    trackEvent(TaskEvents.addNewTaskButtonClickedFromDischargePlan, {
      source: AnalyticsSourceValues.DISCHARGE_PLAN_TAB,
      patientId,
    });
    setShowAddTask(true);
  };

  const handleChangeTaskStatus = (
    taskId: string,
    status: string | null,
    voidReasonExternalId?: string,
  ) => {
    updateTaskStatus({
      dischargePlanId: selectedEpisode?.dischargePlan?.id ?? '',
      taskId,
      taskStatusCode: status,
      voidReasonExternalId,
    });
  };

  // Rely on the upstream ErrorBoundary.
  // We don't want any interaction with Discharge plan when there is missing data or API errors.
  if (isTreatmentEpisodesError || isGetDischargePlanError) {
    let message: string = '';
    if (isTreatmentEpisodesError) {
      message = 'treatment episode';
    } else if (isGetDischargePlanError) {
      message = 'discharge plan';
    }
    throw new Error(`An error occurred while loading the ${message}.`);
  }

  return (
    <DischargePlanViewContextProvider>
      <DischargePlanHeaderSection
        dischargePlanData={dischargePlanData}
        isDischarged={isDischarged}
        isStartButtonDisabled={!selectedEpisode || isDischarged}
      />
      {showEmptyState && (
        <EmptySectionCard
          caption={
            !isDischarged && isNotStarted
              ? 'No discharge plan has been started for this patient'
              : 'The patient must have an active treatment episode before a discharge plan can be started'
          }
          id="discharge-plan-empty-state"
        />
      )}
      <ChakraVStack gap="36px" width="100%">
        {showEpisodeDetails && (
          <Section id="treatment-episodes-details-section">
            <SectionHeader headerText="Treatment episode details" />
            <TreatmentEpisodeDetailsSectionCard
              isLoading={isTreatmentEpisodesLoading}
              treatmentEpisode={selectedEpisode}
              insuranceAuthorizationEndDate={insuranceAuthorizationEndDate}
            />
            <ChakraVStack gap="6px" marginTop="12px" width="100%" alignItems="flex-start">
              <SectionHeader
                headerText="Last attended session"
                headerTextColor="neutral.secondary"
                variant="caption"
              />
              <LastAttendedSectionCard patientId={patientId} />
            </ChakraVStack>
          </Section>
        )}
        {showDischargePlanContent && (
          <>
            <Section id="discharge-details-section">
              <ChakraFlex alignItems="center" justifyContent="space-between">
                <Text variant="h5">Discharge details</Text>
                {isEditable && (
                  <FireflyButton
                    ariaLabel="edit-discharge-details"
                    id="edit-discharge-details"
                    leftIcon={FAIconName.penRegular}
                    onClick={() => setShowEditDischargeDetailsModal(true)}
                    variant="secondary"
                  >
                    Edit
                  </FireflyButton>
                )}
              </ChakraFlex>
              <DischargeDetailsSectionCard
                dischargePlanData={dischargePlanData}
                showSkeleton={isGetDischargePlanLoading}
              />
            </Section>
            {isEditable && (
              <Section id="tasks-section">
                <Tasks
                  buttonOptions={{
                    hideMoreButtonText: 'Hide completed or voided tasks',
                    showMoreButtonText: 'Show completed or voided tasks',
                  }}
                  id="discharge-plan-tasks"
                  isLoading={
                    isDischargePlanTasksLoading || isDischargePlanTasksFetching
                  }
                  onAdd={handleAddTask}
                  onChangeStatus={handleChangeTaskStatus}
                  tasks={taskProps}
                  patientId={patientId}
                />
              </Section>
            )}
            <AftercareAndReferralsSections
              dischargePlanData={dischargePlanData}
            />
            <EditDischargeDetailsModalForm
              dischargePlanData={dischargePlanData}
              isOpen={showEditDischargeDetailsModal}
              onClose={() => setShowEditDischargeDetailsModal(false)}
            />
          </>
        )}
      </ChakraVStack>
      {isAddTaskOpen && (
        <NewTaskAccordion
          defaultCategoryName="Patient care"
          defaultPatient={patient}
          dischargePlanId={selectedEpisode?.dischargePlan?.id}
          dismissAddTask={() => {
            setShowAddTask(false);
          }}
          triggerSource={CREATE_TASK_SOURCES.DISCHARGE_PLAN_VIEW}
          isExpanded={isAddTaskExpanded}
          setIsExpanded={setIsAddTaskExpanded}
          showLinkedPatient
        />
      )}
    </DischargePlanViewContextProvider>
  );
};

export default DischargePlanViewSections;
